import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useSite } from '../../context/SiteContext';
import { categoryAPI } from '../../services/api';
import './Header.css';

const Header = () => {
  const { settings } = useSite();
  const [menuOpen, setMenuOpen]       = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [categories, setCategories]   = useState([]);
  const location = useLocation();
  const navRef = useRef(null);

  // Chargement dynamique des catégories actives
  useEffect(() => {
    categoryAPI.list()
      .then(({ data }) => {
        const active = (data.data || [])
          .filter(c => c.is_active)
          .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
        setCategories(active);
      })
      .catch(() => {});
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location]);

  useEffect(() => {
    const handler = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setCatalogOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const nav = [
    { label: 'Catalogue',  to: '/catalogue', hasDropdown: true },
    { label: 'Techniques', to: '/techniques' },
    { label: 'Nos réalisations', to: '/blog' },
    { label: 'FAQ',        to: '/faq' },
    { label: 'Contact',    to: '/contact' },
  ];

  return (
    <header className={`site-header scrolled${menuOpen ? ' menu-open' : ''}`}>
      <div className="header-inner container">
        {/* Logo — logo_dark sur fond sombre, site_logo sur fond clair */}
        <Link to="/" className="site-logo" aria-label={settings.site_name}>
          {settings.site_logo
            ? <img src={settings.site_logo} alt={settings.site_name} className="logo-img" />
            : <div className="logo-text">
                <span className="logo-mark">P</span>
                <span className="logo-name">{settings.site_name || 'ARTIMARQ'}</span>
              </div>
          }
        </Link>

        {/* Nav desktop */}
        <nav className="header-nav" ref={navRef} aria-label="Navigation principale">
          {nav.map((item) =>
            item.hasDropdown ? (
              <div
                key={item.label}
                className={`nav-item has-dropdown${catalogOpen ? ' open' : ''}`}
                onMouseEnter={() => setCatalogOpen(true)}
                onMouseLeave={() => setCatalogOpen(false)}
              >
                <NavLink
                  to={item.to}
                  className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
                  onClick={() => setCatalogOpen(false)}
                >
                  {item.label}
                  <svg className="dropdown-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </NavLink>
                <div className="nav-dropdown">
                  <div className="dropdown-header">
                    <p>Parcourir par catégorie</p>
                  </div>
                  <div className="dropdown-grid">
                    {categories.map((cat) => (
                      <Link key={cat.id} to={`/catalogue/${cat.slug}`} className="dropdown-item">
                        {cat.name}
                      </Link>
                    ))}
                    <Link to="/catalogue" className="dropdown-item dropdown-all">
                      Tout voir →
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={item.label}
                to={item.to}
                className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        {/* CTA desktop */}
        <div className="header-actions">
          <Link to="/devis-rapide" className="btn-quick-quote">
            Devis rapide
          </Link>
          <Link to="/catalogue" className="btn-studio">
            Studio →
          </Link>
        </div>

        {/* Burger mobile */}
        <button
          className="menu-toggle"
          aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span /><span /><span />
        </button>
      </div>

      {/* Menu mobile */}
      <div className={`mobile-menu${menuOpen ? ' open' : ''}`} aria-hidden={!menuOpen}>
        <nav className="mobile-nav">
          {nav.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`}
            >
              {item.label}
            </NavLink>
          ))}
          {categories.length > 0 && (
            <>
              <div className="mobile-nav-sep" />
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/catalogue/${cat.slug}`}
                  className="mobile-nav-link mobile-nav-sub"
                >
                  {cat.name}
                </Link>
              ))}
            </>
          )}
          <div className="mobile-nav-sep" />
          <Link to="/devis-rapide" className="mobile-nav-link accent">Devis rapide</Link>
          <Link to="/catalogue" className="mobile-nav-link primary">Studio personnalisation</Link>
        </nav>
      </div>
    </header>
  );
};

export default Header;